import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { listEmailPreferences, updateEmailPreference, type NotificationType } from '../api/notifications';
import { PageHeader } from '../components/ui/PageHeader';
import { Card, CardBody } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';

// Every user can reach this page (no @Roles restriction, same as the bell
// itself) - it only ever touches their own preferences, never anyone
// else's. Per Perry: the in-app bell notification always fires regardless
// of what's set here - this page only controls whether the *email* copy of
// each event also gets sent.
export function NotificationPreferencesPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const prefsQuery = useQuery({ queryKey: ['notification-preferences'], queryFn: listEmailPreferences });

  const toggleMutation = useMutation({
    mutationFn: ({ type, emailEnabled }: { type: NotificationType; emailEnabled: boolean }) =>
      updateEmailPreference(type, emailEnabled),
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data);
    },
    onError: () => showToast('Could not save that preference - please try again.', 'error'),
  });

  return (
    <div>
      <PageHeader
        title="Email preferences"
        description="Choose which events ATLAS emails you about. In-app notifications (the bell icon) always show regardless of these settings."
      />

      <Card>
        {prefsQuery.isLoading ? (
          <CardBody className="flex justify-center py-10">
            <Spinner />
          </CardBody>
        ) : !prefsQuery.data || prefsQuery.data.length === 0 ? (
          <CardBody>
            <p className="text-sm text-slate-500">No notification types apply to your account.</p>
          </CardBody>
        ) : (
          <div className="divide-y divide-slate-100">
            {prefsQuery.data.map((pref) => (
              <div key={pref.type} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="text-sm text-slate-800">{pref.label}</span>
                </div>
                <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={pref.emailEnabled}
                    disabled={toggleMutation.isPending}
                    onChange={(e) => toggleMutation.mutate({ type: pref.type, emailEnabled: e.target.checked })}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-slate-200 transition-colors peer-checked:bg-brand-600 peer-disabled:opacity-50" />
                  <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                </label>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
